import { useCallback, useEffect, useState } from 'react';
import { readLimitedBytes, requestJson, runWithDeadline } from '../utils/asyncRequest';

const emptyResources = () => ({ instruments: [], status: null, definitions: null, kmls: null, descriptionImageUrl: null });
const list = payload => {
    if (payload?.status !== 1 || !Array.isArray(payload.message)) throw new Error('服務未回傳有效清單。');
    return payload.message;
};

export function dashboardRequests(projectId, token, hasDescriptionImage, signal, timeoutMs) {
    const base = process.env.REACT_APP_WEBAPI_HOST;
    const id = encodeURIComponent(projectId);
    const options = { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' };
    const limits = { signal, timeoutMs };
    const json = path => requestJson(base + path, options, limits);
    return [
        { key: 'instruments', label: '儀器位置', load: () => json(`/InstrumentExtends/Projects/${id}?depthSeriesConfigId=`).then(list) },
        { key: 'status', label: '監測資料', load: async () => {
            const value = await json(`/SiteData/Projects/${id}/LatestDataWithAlert?lang=zh-tw&bypassCache=false`);
            if (!Array.isArray(value?.entries)) throw new Error('服務未回傳有效監測資料。');
            return value;
        } },
        { key: 'definitions', label: '儀器定義', load: async () => {
            const value = await json(`/SiteData/Projects/${id}/GageDefinition?onlyInParabase=false&lang=zh-tw&bypassCache=false`);
            if (!Array.isArray(value?.items)) throw new Error('服務未回傳有效儀器定義。');
            return value;
        } },
        { key: 'kmls', label: 'KML 清單', load: () => json(`/ProjectKmls/${id}`).then(list) },
        { key: 'descriptionImageUrl', label: '專案說明圖片', load: () => !hasDescriptionImage ? Promise.resolve(null) : runWithDeadline(async requestSignal => {
            const response = await fetch(`${base}/Projects/${id}/GetDescriptionImageStream`, { ...options, signal: requestSignal });
            if (response.status === 404) return null;
            if (!response.ok) throw new Error(`圖片請求失敗（HTTP ${response.status}）。`);
            const type = response.headers.get('content-type') || '';
            if (!type.startsWith('image/')) throw new Error('圖片格式不正確。');
            return new Blob([await readLimitedBytes(response, 10 * 1024 * 1024)], { type });
        }, limits) }
    ];
}

export default function useDashboardResources(projectId, token, hasDescriptionImage, timeoutMs = 20000) {
    const [state, setState] = useState({ projectId: null, resources: emptyResources(), errors: [], loading: false });
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => setAttempt(value => value + 1), []);

    useEffect(() => {
        if (!projectId || !token) return;
        const controller = new AbortController();
        const urls = [];
        let active = true;
        const requests = dashboardRequests(projectId, token, hasDescriptionImage, controller.signal, timeoutMs);
        let pending = requests.length;
        setState({ projectId, resources: emptyResources(), errors: [], loading: true });
        // Publish each successful resource immediately. A failed optional resource
        // cannot discard other results, and every request has its own deadline.
        requests.forEach(({ key, label, load }) => {
            Promise.resolve().then(load).then(value => {
                if (!active) return;
                if (value instanceof Blob) { value = URL.createObjectURL(value); urls.push(value); }
                setState(previous => ({ ...previous, resources: { ...previous.resources, [key]: value } }));
            }).catch(error => {
                if (!active || error.name === 'AbortError') return;
                setState(previous => ({ ...previous, errors: [...previous.errors, `${label}：${error.message}`] }));
            }).finally(() => {
                pending -= 1;
                if (active && pending === 0) setState(previous => ({ ...previous, loading: false }));
            });
        });
        return () => { active = false; controller.abort(); urls.forEach(url => URL.revokeObjectURL(url)); };
    }, [projectId, token, hasDescriptionImage, timeoutMs, attempt]);

    // Do not flash data from the previous project before its effect cleanup runs.
    return { ...(state.projectId === projectId ? state : { resources: emptyResources(), errors: [], loading: Boolean(projectId) }), retry };
}
